import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { GENDERS, GOALS, KINKS } from "@/constants";
import ProfileCard from "@/components/ProfileCard";
import { useSocket } from "@/context/SocketContext";
import { toast } from "sonner";
import { Search, Layers, LayoutGrid, SlidersHorizontal, Crown } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { ProfileNudgeBanner } from "@/components/ProfileNudgeBanner";
import { TrialBanner } from "@/components/TrialBanner";
import { SwipeDeck } from "@/components/SwipeDeck";

const PHONE_Q = "(max-width: 767px)";
const useIsPhone = () => {
  const [m, setM] = useState(() => window.matchMedia(PHONE_Q).matches);
  useEffect(() => {
    const mq = window.matchMedia(PHONE_Q);
    const on = (e) => setM(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
};

const ViewToggle = ({ view, onPick }) => (
  <div data-testid="discover-view-toggle" className="flex rounded-full border border-white/10 p-1">
    {[["cards", Layers, "Cards"], ["grid", LayoutGrid, "Grid"]].map(([v, I, l]) => (
      <button key={v} data-testid={`discover-view-${v}`} onClick={() => onPick(v)} aria-pressed={view === v}
        className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.15em] transition-colors ${view === v ? "bg-gold/15 text-gold" : "text-text-muted"}`}>
        <I size={13} /> {l}
      </button>
    ))}
  </div>
);

export default function Browse() {
  const [profiles, setProfiles] = useState([]);
  const [q, setQ] = useState("");
  const [gender, setGender] = useState("");
  const [goal, setGoal] = useState("");
  const [kink, setKink] = useState("");
  const [founding, setFounding] = useState(false);
  const [loading, setLoading] = useState(true);
  const { showMatch } = useSocket();
  const phone = useIsPhone();
  const [view, setView] = useState(() => localStorage.getItem("velluto_discover_view") || "cards");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loadKey, setLoadKey] = useState(0);
  const cards = phone && view === "cards";
  const pickView = (v) => { setView(v); localStorage.setItem("velluto_discover_view", v); };

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (gender) p.set("gender", gender);
    if (goal) p.set("goal", goal);
    if (kink) p.set("kink", kink);
    if (founding) p.set("founding", "true");
    const { data } = await api.get(`/profiles?${p.toString()}`);
    setProfiles(data);
    setLoadKey((k) => k + 1);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [gender, goal, kink, founding]);

  const like = async (profile) => {
    try {
      const { data } = await api.post(`/likes/${profile.id}`);
      setProfiles((ps) => ps.map((p) => (p.id === profile.id ? { ...p, liked_by_me: data.liked } : p)));
      if (data.matched) showMatch(data.match_user);
      else toast(data.liked ? "Admiration sent" : "Removed");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Couldn't send admiration");
    }
  };

  const superLike = async (profile) => {
    try {
      const { data } = await api.post(`/superlikes/${profile.id}`);
      setProfiles((ps) => ps.map((p) => (p.id === profile.id ? { ...p, liked_by_me: true } : p)));
      if (data.matched) showMatch(data.match_user);
      else toast(`You super-admired ${profile.display_name} ✦`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Couldn't send a Super Admire");
    }
  };

  const chip = (val, cur, setter) => (
    <button
      key={val || "all"}
      data-testid={`filter-${val || "all"}`}
      onClick={() => setter(cur === val ? "" : val)}
      className={`text-xs uppercase tracking-[0.15em] pb-1 border-b transition-colors ${
        cur === val ? "text-text-main border-wine" : "text-text-muted border-transparent hover:text-text-main"
      }`}
    >
      {val || "All"}
    </button>
  );

  const filtering = filtersOpen || gender || goal || kink || founding;

  return (
    <div className="pt-24 pb-24 max-w-7xl mx-auto px-4 sm:px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Discover</p>
        <h1 className={`font-display text-4xl sm:text-6xl font-light mb-6 sm:mb-10 ${cards ? "hidden" : ""}`}>Who's out tonight</h1>
        {!cards && <><TrialBanner /><ProfileNudgeBanner /></>}

        {phone && (
          <div className="flex items-center gap-2 mb-5">
            <ViewToggle view={view} onPick={pickView} />
            <button data-testid="discover-filters-toggle" onClick={() => setFiltersOpen((o) => !o)} aria-expanded={filtersOpen}
              className={`ml-auto flex items-center gap-1.5 px-4 py-2.5 rounded-full border text-[11px] uppercase tracking-[0.15em] transition-colors ${filtering ? "border-gold/50 text-gold" : "border-white/10 text-text-muted"}`}>
              <SlidersHorizontal size={13} /> Filters
            </button>
          </div>
        )}

        <div data-testid="discover-filters" className={phone && !filtersOpen ? "hidden" : ""}>
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative max-w-md mb-8 sm:mb-10">
            <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, vibe, or city"
              className="w-full bg-transparent border-b border-white/15 pl-7 py-3 text-base focus:border-wine outline-none transition-colors"
            />
          </form>

          <div className="space-y-4 mb-10 sm:mb-14">
            <button data-testid="filter-founding" onClick={() => setFounding((f) => !f)} aria-pressed={founding}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-[11px] uppercase tracking-[0.15em] transition-colors ${founding ? "border-gold bg-gold/15 text-gold" : "border-gold/30 text-gold/70 hover:text-gold"}`}>
              <Crown size={13} /> Founding Creators
            </button>
            <div className="flex flex-wrap gap-5">{chip("", gender, setGender)}{GENDERS.map((g) => chip(g, gender, setGender))}</div>
            <div className="flex flex-wrap gap-5">{GOALS.map((g) => chip(g, goal, setGoal))}</div>
            <div className="flex flex-wrap gap-5">{KINKS.map((k) => chip(k, kink, setKink))}</div>
          </div>
        </div>
      </motion.div>

      {!cards && <AdSlot className="mb-10" />}
      {loading ? (
        <p className="text-text-muted">Summoning members…</p>
      ) : profiles.length === 0 ? (
        <p data-testid="no-results" className="text-text-muted font-display text-2xl italic">{founding ? "No Founding Creators here yet." : "No one matches that fantasy yet."}</p>
      ) : cards ? (
        <>
          <SwipeDeck key={loadKey} profiles={profiles} onLike={like} onSuperLike={superLike} />
          <div className="mt-8"><TrialBanner /><ProfileNudgeBanner /></div>
        </>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {profiles.map((p, i) => (
            <ProfileCard key={p.id} profile={p} index={i} onLike={like} onSuperLike={superLike} />
          ))}
        </div>
      )}
    </div>
  );
}
